import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, Socket } from "socket.io-client";
import alarmAudio from "../../assets/audios/alarm-clock.mp3";
import { useAuth } from "../AuthProvider/AuthProvider";
import { useMessagesPopup } from "../MessagesPopupProvider/MessagesPopupProvider";
import { AlarmDialog } from "../../components/Dialogs/AlarmDialog";
import { AlarmSnackbar } from "../../components/Dialogs/AlarmSnackbar";
import { Box, Typography } from "@mui/material";

interface SocketContextType {
  socket: Socket | null;
}

interface AlarmResponse {
  message?: string;
  data: {
    contact_number?: string;
    vehicle_number?: string;
    name?: string;
    form_name?: string;
    assign_to_id?: number;
    mobile_no?: string;
    email?: string;
    complaint_type?: string;
    description?: string;
    userName?: string;
    complaintNo?: number;
    status?: string;
  };
}

interface EventConfig {
  eventName: string;
  condition: (user: any) => boolean;
  message: string;
  shouldShowDialog: (response: AlarmResponse, user: any) => boolean;
  renderContent: (response: AlarmResponse) => React.ReactNode;
  shouldShowSnackbar?: (response: AlarmResponse, user: any) => boolean;
  snackbarDuration?: number | null;
}

const eventConfigs: EventConfig[] = [
  {
    eventName: "newRsaRecord",
    condition: (user) => user.user_id === 177 || user.user_id === 214,
    message: "A new RSA request detected!",
    shouldShowDialog: (response, user) => {
      if (response.data.vehicle_number === "Google") {
        return user.user_id === 214;
      }
      return user.user_id === 177;
    },
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Vehicle No: {response.data.vehicle_number}
        </Typography>
      </>
    ),
  },
  {
    eventName: "ecallGoogleNewRecord",
    condition: (user) => user.user_id === 177,
    message: "A New Google Request Detected!",
    shouldShowDialog: (response, user) => {

      return user.user_id === 177;
    },
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Vehicle No: {response.data.vehicle_number}
        </Typography>
      </>
    ),
  },
  {
    eventName: "newExotelRecord",
    condition: (user) => user.user_id === 177,
    message: "A New Exotal Request Detected!",
    shouldShowDialog: (response, user) => {

      return user.user_id === 177;
    },
    renderContent: (response) => (
      <>

        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>

      </>
    ),
  },
  {
    eventName: "saqibGoogleNewRecord",
    condition: (user) => user.user_id === 214,
    message: "A New Google Request Detected!",
    shouldShowDialog: (response, user) => {

      return user.user_id === 214;
    },
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Vehicle No: {response.data.vehicle_number}
        </Typography>
      </>
    ),
  },
  {
    eventName: "mamtaGoogleNewRecord",
    condition: (user) => user.user_id === 4769,
    message: "A New Google Request Detected!",
    shouldShowDialog: (response, user) => {

      return user.user_id === 4769;
    },
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Vehicle No: {response.data.vehicle_number}
        </Typography>
      </>
    ),
  },
  {
    eventName: "newLeadRecord",
    condition: (user) => user.role === "SALES_TEAM_LEAD",
    message: "A new Lead request detected!",
    shouldShowDialog: (response, user) => response.data.assign_to_id === user.user_id,
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Name: {response.data.name}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.contact_number}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Form Name: {response.data.form_name}
        </Typography>
      </>
    ),
  },
  {
    eventName: "newFreeQuoteRecord",
    condition: (user) => user.user_id === 177,
    message: "A new Quote request detected!",
    shouldShowDialog: () => true,
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Name: {response.data.name}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Contact No: {response.data.mobile_no}
        </Typography>
        <Typography sx={{ mb: 2, fontSize: "20px", fontWeight: "bold" }}>
          Email: {response.data.email}
        </Typography>
      </>
    ),
  },
  {
    eventName: "newUserComplaintRecord",
    condition: (user) => user.role === "CALL_CENTER_LEAD", // Show for all logged-in users
    message: "A new user complaint has been submitted!",
    shouldShowDialog: () => false, // Only show snackbar, no dialog
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 1, fontSize: "14px", fontWeight: "bold" }}>
          Name: {response.data.userName}
        </Typography>

      </>
    ),
    shouldShowSnackbar: () => true,
    snackbarDuration: null,
  },
  {
    eventName: "newNotification",
    condition: (user) => user.isLogin, // Show for all logged-in users
    message: "New notification received!",
    shouldShowDialog: () => false, // Only show snackbar, no dialog
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 1, fontSize: "14px", fontWeight: "bold" }}>
          Title: {response.data.name || "Notification"}
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Details: {response.data.description || "No details available"}
        </Typography>
      </>
    ),
    shouldShowSnackbar: () => true,
  },
  {
    eventName: "newOpenComplaintRecord",
    condition: (user) => user.role === "CONTROL_ROOM_LEAD", // Show for all logged-in users
    // Show for all logged-in users
    message: "New Open Complaint has been submitted!",
    shouldShowDialog: () => true, // Only show snackbar, no dialog
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 1, fontSize: "14px", fontWeight: "bold" }}>
          Title: {response.data.userName || "Notification"}
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Details: {response.data.complaintNo || "No details available"}
        </Typography>
      </>
    ),
    shouldShowSnackbar: () => false,
  },
  {
    eventName: "statusUpdateOfComplaintByVehicleCare",
    condition: (user) =>
      user.role === "CONTROL_ROOM_LEAD" ||
      user.role === "CONTROL_ROOM_MANAGER",
    message: "Complaint Status Updated by Vehicle Care",
    shouldShowDialog: () => true,
    renderContent: (response) => (
      <>
        <Typography sx={{ mb: 1, fontSize: "16px", fontWeight: "bold" }}>
          {response.message}
        </Typography>
        <Typography sx={{ mb: 1, fontSize: "14px", fontWeight: "medium" }}>
          Complaint No: {response.data.complaintNo}
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Status: {response.data.status}
        </Typography>
      </>
    ),
    shouldShowSnackbar: () => false,
  },
];

const UnifiedSocketContext = createContext<SocketContextType>({ socket: null });

export const useUnifiedSocket = () => useContext(UnifiedSocketContext);

export const UnifiedSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { showTopSuccessSnackbar } = useMessagesPopup();
  const socketRef = useRef<Socket | null>(null);
  const [isAlarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [alarmResponse, setAlarmResponse] = useState<AlarmResponse | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Snackbar states
  const [isAlarmSnackbarOpen, setAlarmSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarContent, setSnackbarContent] = useState<React.ReactNode | null>(null);
  const [snackbarDuration, setSnackbarDuration] = useState<number | null>(5000);

  // Preload the audio when the component mounts
  useEffect(() => {
    audioRef.current = new Audio(alarmAudio);
  }, []);

  const backendUrl = process.env.REACT_APP_SERVER_URL;
  const finalUrl = backendUrl?.replace(/\/api$/, "").trim();

  useEffect(() => {
    if (user.isLogin && user.tokens?.access) {
      socketRef.current = io(finalUrl as string, {
        query: { token: user.tokens.access },
      });

      // Set up event listeners based on configuration
      eventConfigs.forEach((config) => {
        if (config.condition(user)) {
          socketRef.current?.on(config.eventName, async (response) => {
            // Always show the snackbar message

            // Only show dialog and play audio if shouldShowDialog returns true
            if (config.shouldShowDialog(response, user)) {
              setAlarmDialogOpen(true);
              setAlarmResponse(response);

              try {
                if (audioRef.current) {
                  await audioRef.current.play();
                  audioRef.current.loop = true;
                }
              } catch (err) {
                console.error("Failed to play audio:", err);
              }
            } else if (config.shouldShowSnackbar && config.shouldShowSnackbar(response, user)) {
              // Show alarm snackbar with information
              setSnackbarMessage(config.message);
              setSnackbarContent(config.renderContent(response));
              setSnackbarDuration(config.snackbarDuration !== undefined ? config.snackbarDuration : 5000);
              setAlarmSnackbarOpen(true);
            }
          });
        }
      });
    }

    return () => {
      // Clean up all event listeners
      eventConfigs.forEach((config) => {
        socketRef.current?.off(config.eventName);
      });
    };
  }, [user, showTopSuccessSnackbar]);

  const handleCloseAlarmDialog = () => {
    setAlarmDialogOpen(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handleCloseAlarmSnackbar = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlarmSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarContent(null);
    setSnackbarDuration(5000);
  };

  const renderDialogContent = () => {
    if (!alarmResponse) return null;

    // Find the matching event config based on the response data
    const matchingConfig = eventConfigs.find((config) => {
      const eventData = alarmResponse.data;
      if (config.eventName === "newRsaRecord" && eventData.vehicle_number) return true;
      if (config.eventName === "ecallGoogleNewRecord" && eventData.contact_number) return true;
      if (config.eventName === "newExotelRecord" && eventData.contact_number) return true;
      if (config.eventName === "saqibGoogleNewRecord" && eventData.contact_number) return true;
      if (config.eventName === "newLeadRecord" && eventData.name) return true;
      if (config.eventName === "newFreeQuoteRecord" && eventData.email) return true;
      if (config.eventName === "newUserComplaintRecord" && eventData.complaint_type) return true;
      if (config.eventName === "statusUpdateOfComplaintByVehicleCare" && eventData.complaintNo) return true;
      return false;
    });

    return (
      <Box>
        {matchingConfig ? matchingConfig.renderContent(alarmResponse) : null}
      </Box>
    );
  };

  return (
    <UnifiedSocketContext.Provider value={{ socket: socketRef.current }}>
      {children}
      <AlarmDialog
        open={isAlarmDialogOpen}
        content={renderDialogContent()}
        onClose={handleCloseAlarmDialog}
      />
      <AlarmSnackbar
        open={isAlarmSnackbarOpen}
        message={snackbarMessage}
        content={snackbarContent}
        onClose={handleCloseAlarmSnackbar}
        autoHideDuration={snackbarDuration}
      />
    </UnifiedSocketContext.Provider>
  );
}; 